import DocumentMeta from 'react-document-meta';
import './tokens.css';
import styles from './login.module.css';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../header/index';
import { FaEye } from "react-icons/fa";
import ErrorMessage from '../../package/error/index';
import DotLoading from './loading';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { getApps, initializeApp } from 'firebase/app';
import AppPreview from './items/appPreview';

// Firebase 初期化はコンポーネント外で
// この静的importはESモジュールの評価順の都合で src/index.js より先に実行され得るため、
// src/index.js と同じ条件分岐(E2E時はEmulator用の固定config)にして、
// どちらが先に initializeApp を呼んでも同一のconfigになるようにしている。
// (config不一致だと2回目の呼び出しで "app/duplicate-app" 例外が発生するため)
const useEmulator = process.env.REACT_APP_USE_EMULATOR === 'true';
const firebaseConfig = useEmulator
  ? {
      apiKey: 'demo-api-key',
      authDomain: 'localhost',
      projectId: 'demo-memorii-e2e',
      storageBucket: 'demo-memorii-e2e.appspot.com',
    }
  : {
      apiKey: process.env.REACT_APP_APIKEY,
      authDomain: process.env.REACT_APP_AUTHDOMAIN,
      databaseURL: process.env.REACT_APP_DATABASEURL,
      projectId: process.env.REACT_APP_PROJECT_ID,
      storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_APP_ID,
      measurementId: process.env.REACT_APP_MEASUREMENT_ID,
    };

if (!getApps().length) {
  initializeApp(firebaseConfig);
}

type Mode = 'verifyEmail' | 'resetPassword' | 'recoverEmail' | 'unknown';

// Firebase の action mode は verifyEmail / resetPassword / recoverEmail の
// いずれも同じ URL パターン（?mode=...&oobCode=...）で届くため、1画面で分岐する。
// 参考: https://firebase.google.com/docs/auth/custom-email-handler
type Status = 'checking' | 'verifyEmailDone' | 'resetForm' | 'resetDone' | 'recoverEmailDone' | 'error';

const AuthAction: React.FC = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const mode = (searchParams.get('mode') as Mode) || 'unknown';
  const oobCode = searchParams.get('oobCode') || '';

  const [status, setStatus] = useState<Status>('checking');
  const [errorMessage, setErrorMessage] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordEye, setPasswordEye] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode || mode === 'unknown') {
      setErrorMessage('URLが無効です。メール内のリンクからもう一度お試しください。');
      setStatus('error');
      return;
    }

    const run = async () => {
      try {
        if (mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);
          setStatus('verifyEmailDone');
        } else if (mode === 'resetPassword') {
          const email = await verifyPasswordResetCode(auth, oobCode);
          setResetEmail(email);
          setStatus('resetForm');
        } else if (mode === 'recoverEmail') {
          await checkActionCode(auth, oobCode);
          await applyActionCode(auth, oobCode);
          setStatus('recoverEmailDone');
        }
      } catch (error: any) {
        switch (error.code) {
          case 'auth/expired-action-code':
            setErrorMessage('このリンクの有効期限が切れています。もう一度お試しください。');
            break;
          case 'auth/invalid-action-code':
            setErrorMessage('このリンクはすでに使用されているか、無効です。');
            break;
          default:
            setErrorMessage('処理に失敗しました。時間をおいて再度お試しください。');
        }
        setStatus('error');
      }
    };

    run();
  }, [auth, mode, oobCode]);

  const submitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (password.length <= 7) {
      setErrorMessage('パスワードを8文字以上入力してください。');
      return;
    }
    if (password !== passwordConfirm) {
      setErrorMessage('パスワードが一致しません。');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('resetDone');
    } catch (error: any) {
      setErrorMessage('パスワードの更新に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  const meta = {
    title: "認証確認 | 株式会社memorii（memorii works）",
    description: "株式会社memoriiが提供する『memorii works』のメール認証・パスワード再設定の確認ページです。",
    canonical: "https://memorii-works.com/company/action",
    meta: { charset: "utf-8", name: { robots: 'noindex,nofollow,noarchive,noimageindex' } }
  };

  return (
    <DocumentMeta {...meta}>
      <div className={styles.page}>

        <Header />

        <main className={styles.wrap}>
          <div className={styles.card}>

            <section className={styles.brand}>
              <div className={styles.brandInner}>
                <p className={styles.brandTagline}>
                  美容業界の新卒採用を、<br />
                  ひとつの仕組みに。
                </p>
                <AppPreview />
              </div>
            </section>

            <section className={styles.panel}>

              {status === 'checking' && (
                <div className={styles.state}>
                  <DotLoading text="確認中" />
                </div>
              )}

              {status === 'error' && (
                <div className={styles.state}>
                  <ErrorMessage message={errorMessage} />
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                    ログイン画面はこちら
                  </button>
                </div>
              )}

              {status === 'verifyEmailDone' && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    メールアドレスの認証が完了しました。<br />
                    続けてログインしてください。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                    ログイン画面はこちら
                  </button>
                </div>
              )}

              {status === 'recoverEmailDone' && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    メールアドレスの変更を取り消しました。<br />
                    心当たりがない場合は、お手数ですがパスワードの再設定をお願いします。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/reset')}>
                    パスワードの再設定はこちら
                  </button>
                </div>
              )}

              {status === 'resetDone' && (
                <div className={styles.state}>
                  <p className={styles.stateText}>
                    パスワードを再設定しました。<br />
                    新しいパスワードでログインしてください。
                  </p>
                  <button type="button" className={styles.linkBtn} onClick={() => navigate('/company/login')}>
                    ログイン画面はこちら
                  </button>
                </div>
              )}

              {status === 'resetForm' && (
                <form className={styles.form} onSubmit={submitNewPassword}>
                  <h1 className={styles.title}>パスワード再設定</h1>

                  {resetEmail && (
                    <p className={styles.tabsLabel} style={{ marginBottom: 'var(--mw-space-4)' }}>
                      {resetEmail} の新しいパスワードを設定してください
                    </p>
                  )}

                  {errorMessage && <ErrorMessage message={errorMessage} />}

                  <div className={styles.field}>
                    <label htmlFor="action-password" className={styles.label}>新しいパスワード</label>
                    <div className={styles.inputWrap}>
                      <input
                        id="action-password"
                        className={styles.input}
                        type={passwordEye ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="8文字以上"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        className={styles.eye}
                        aria-label={passwordEye ? 'パスワードを隠す' : 'パスワードを表示'}
                        aria-pressed={passwordEye}
                        onClick={() => setPasswordEye(!passwordEye)}
                      >
                        <FaEye />
                      </button>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="action-password-confirm" className={styles.label}>新しいパスワード（確認）</label>
                    <input
                      id="action-password-confirm"
                      className={styles.input}
                      type={passwordEye ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="8文字以上"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                    />
                  </div>

                  <button type="submit" className={styles.submit} disabled={submitting}>
                    {submitting ? '更新中...' : 'パスワードを更新する'}
                  </button>
                </form>
              )}

            </section>

          </div>
        </main>

      </div>
    </DocumentMeta>
  );
}

export default AuthAction;
